// iOS print helper utilities
// Provides iOS detection, iframe-based printing (avoids popup blockers),
// and Bluetooth capability detection

/**
 * Detect if running on iOS (iPhone, iPad, iPod)
 * Handles iPadOS 13+ which reports as "MacIntel"
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Check if Web Bluetooth API is available and functional
 */
export const canUseBluetooth = () => {
  return 'bluetooth' in navigator && navigator.bluetooth && typeof navigator.bluetooth.requestDevice === 'function';
};

/**
 * Print HTML content via a hidden iframe.
 * Works on iOS Safari without popup blockers (unlike window.open).
 *
 * @param {HTMLElement} htmlElement - DOM node to print (e.g. printRef.current)
 * @returns {Promise<void>}
 */
export const printViaIframe = (htmlElement) => {
  return new Promise((resolve, reject) => {
    if (!htmlElement) {
      reject(new Error('No content to print'));
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '320px';
    iframe.style.height = '0';
    iframe.style.border = 'none';

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(`
      <html>
      <head>
        <style>
          body {
            margin: 0;
            padding: 8px;
            font-family: 'Courier New', 'Lucida Console', 'Monaco', monospace;
            background: #fff;
            color: #000;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        ${htmlElement.innerHTML}
      </body>
      </html>
    `);
    iframeDoc.close();

    // Wait for content to render before printing
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        reject(err);
        return;
      }

      // Clean up after a delay to allow print dialog to appear
      setTimeout(() => {
        document.body.removeChild(iframe);
        resolve();
      }, 1000);
    };

    // Fallback: if onload doesn't fire (some browsers), try after a short delay
    setTimeout(() => {
      if (iframe.parentNode) {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (err) {
          // Already handled or will be handled
        }
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
            resolve();
          }
        }, 1000);
      }
    }, 500);
  });
};
